/**
 * Wizard 2 State Manager
 *
 * Same as wizard-1 state but with:
 * - selectedFlavorsTrial, selectedFlavorsFull (separate arrays)
 * - selectedPlan: bulk multiplier only — '1', '2', or '3'
 * - planBowlValue: Shopify "Kom" option value (e.g. Volledig / Half), from product options
 */

function normalizePetNameEdges(value) {
  if (typeof value !== 'string') return value;
  return value.replace(/^[\s\uFEFF\u00A0]+|[\s\uFEFF\u00A0]+$/g, '');
}
window.normalizePetNameEdges = normalizePetNameEdges;

class PetFlowState {
  constructor(flowName = 'wizard-2') {
    this.flowName = flowName;
    this.currentStep = 'name';
    this.petData = {
      name: '',
      gender: '',
      spayed: null,
      ageGroup: '',
      breed: '',
      breedUnknown: false,
      breedMixed: false,
      weight: null,
      composition: '',
      activity: '',
      isWorkingDog: false,
      allergies: [],
      snacks: '',
      ownerName: '',
      ownerEmail: '',
      emailConsent: true,
      profilePhotoUrl: '',
    };

    this.calculations = {
      dailyKcal: null,
      dailyFoodG: null,
      daysPerBag: null,
      bagCount: null,
      matchDays: null,
      introBagCount: null,
      introMatchDays: null
    };

    this.selectedFlavorsTrial = []; // Trial delivery flavors (rund, kip, vis)
    this.selectedFlavorsFull = [];  // Full plan flavors
    this.selectedPlan = null;      // '1', '2', '3' — bulk / shipment frequency multiplier only
    /** @type {string|null} Selected Shopify product option value for bowl size (Kom), storefront-translated */
    this.planBowlValue = null;
    /** @type {string[]|null} Ordered values from Shopify for the bowl option (for half detection) */
    this.planBowlOptionValues = null;
    this.selectedPricePeriod = 'day';
    this.petMetaobjectId = null;
    this.customerId = null;
    this.sessionCustomerId = null;
    this.sessionOwnerName = null;
    this.sessionOwnerEmail = null;
    this.sessionEmailConsent = true;
    /** User continued past the email step without an address (Wizard 2 only). */
    this.skippedEmailWithoutAddress = false;
    /**
     * Last explicit marketing consent choice on the email step (Wizard 2).
     * null = no choice yet → UI defaults to opted in when the consent row is shown.
     */
    this.wizard2EmailMarketingConsentPreference = null;
    this.variantPricing = {};
    this.variantPricingHalf = {};
    /** ISO 4217 from Storefront variant prices (e.g. HUF); fallback to Shopify.currency.active in formatters */
    this.presentmentCurrencyCode = null;
    this.sellingPlanNames = {};
    /** Last trial-bundle discount % shown in the offer bar (after plan_overview succeeded). */
    this.wizard2OfferBarPct = null;
    /** `buildWizard2PricingFingerprint` output when wizard2OfferBarPct was saved. */
    this.wizard2OfferBarFingerprint = null;
  }

  updatePetData(field, value) {
    if (field in this.petData) {
      if (field === 'name' && typeof value === 'string') {
        value = normalizePetNameEdges(value);
      }
      this.petData[field] = value;
      this.notifyTraits();
    } else {
      console.warn(`Unknown pet data field: ${field}`);
    }
  }

  notifyTraits() {
    try {
      if (window.WizardGa4Traits && window.WizardGa4Traits.notify) window.WizardGa4Traits.notify();
    } catch (e) {}
  }

  updatePetDataBatch(updates) {
    Object.keys(updates).forEach(field => {
      this.updatePetData(field, updates[field]);
    });
  }

  updateCalculations(calculations) {
    this.calculations = { ...this.calculations, ...calculations };
  }

  setCurrentStep(step) {
    this.currentStep = step;
  }

  getCurrentStep() {
    return this.currentStep;
  }

  validateStep(step = this.currentStep) {
    switch (step) {
      case 'name':
        return this.petData.name.trim().length > 0 && this.petData.gender !== '';
      case 'spayed':
        return this.petData.spayed !== null;
      case 'age':
        return this.petData.ageGroup !== '';
      case 'breed':
        return this.petData.breed.trim().length > 0 || this.petData.breedUnknown || this.petData.breedMixed;
      case 'weight':
        return this.petData.weight !== null && this.petData.weight > 0;
      case 'composition':
        return this.petData.composition !== '';
      case 'activity':
        return this.petData.activity !== '';
      case 'allergies':
        return true;
      case 'snacks':
        return this.petData.snacks !== '';
      case 'email':
        if (this.skippedEmailWithoutAddress) return true;
        return this.isValidEmail(this.petData.ownerEmail);
      default:
        return true;
    }
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  getPetData() {
    return { ...this.petData };
  }

  getCalculations() {
    return { ...this.calculations };
  }

  getState() {
    return {
      currentStep: this.currentStep,
      petData: this.getPetData(),
      calculations: this.getCalculations(),
      selectedFlavorsTrial: [...this.selectedFlavorsTrial],
      selectedFlavorsFull: [...this.selectedFlavorsFull],
      selectedPlan: this.selectedPlan,
      planBowlValue: this.planBowlValue,
      planBowlOptionValues: this.planBowlOptionValues ? [...this.planBowlOptionValues] : null,
      selectedPricePeriod: this.selectedPricePeriod,
      petMetaobjectId: this.petMetaobjectId,
      customerId: this.customerId,
      sessionCustomerId: this.sessionCustomerId,
      sessionOwnerName: this.sessionOwnerName,
      sessionOwnerEmail: this.sessionOwnerEmail,
      sessionEmailConsent: this.sessionEmailConsent,
      skippedEmailWithoutAddress: this.skippedEmailWithoutAddress,
      wizard2EmailMarketingConsentPreference: this.wizard2EmailMarketingConsentPreference,
      variantPricing: { ...this.variantPricing },
      variantPricingHalf: { ...this.variantPricingHalf },
      presentmentCurrencyCode: this.presentmentCurrencyCode,
      sellingPlanNames: { ...this.sellingPlanNames },
      wizard2OfferBarPct: this.wizard2OfferBarPct,
      wizard2OfferBarFingerprint: this.wizard2OfferBarFingerprint
    };
  }

  loadState(state) {
    if (state.currentStep && state.currentStep !== 'get_started') this.currentStep = state.currentStep;
    if (state.petData) this.petData = { ...this.petData, ...state.petData };
    if (typeof this.petData.name === 'string') {
      this.petData.name = normalizePetNameEdges(this.petData.name);
    }
    if (state.calculations) this.calculations = { ...this.calculations, ...state.calculations };
    if (state.selectedFlavorsTrial) this.selectedFlavorsTrial = [...state.selectedFlavorsTrial];
    if (state.selectedFlavorsFull) this.selectedFlavorsFull = [...state.selectedFlavorsFull];
    if (state.selectedPlan) this.selectedPlan = state.selectedPlan;
    if (state.planBowlValue !== undefined && state.planBowlValue !== null) this.planBowlValue = state.planBowlValue;
    if (state.planBowlOptionValues && state.planBowlOptionValues.length) {
      this.planBowlOptionValues = [...state.planBowlOptionValues];
    }
    if (state.selectedPricePeriod) this.selectedPricePeriod = state.selectedPricePeriod;
    if (state.petMetaobjectId) this.petMetaobjectId = state.petMetaobjectId;
    if (state.customerId) this.customerId = state.customerId;
    if (state.sessionCustomerId) this.sessionCustomerId = state.sessionCustomerId;
    if (state.sessionOwnerName !== undefined) this.sessionOwnerName = state.sessionOwnerName;
    if (state.sessionOwnerEmail !== undefined) this.sessionOwnerEmail = state.sessionOwnerEmail;
    if (state.sessionEmailConsent !== undefined) this.sessionEmailConsent = state.sessionEmailConsent;
    if (state.skippedEmailWithoutAddress !== undefined) {
      this.skippedEmailWithoutAddress = state.skippedEmailWithoutAddress;
    }
    if (state.wizard2EmailMarketingConsentPreference === true || state.wizard2EmailMarketingConsentPreference === false) {
      this.wizard2EmailMarketingConsentPreference = state.wizard2EmailMarketingConsentPreference;
    } else if (
      !(this.wizard2EmailMarketingConsentPreference === true || this.wizard2EmailMarketingConsentPreference === false)
    ) {
      try {
        const r = localStorage.getItem('wizard-2-email-marketing-consent-preference');
        if (r === '1') this.wizard2EmailMarketingConsentPreference = true;
        else if (r === '0') this.wizard2EmailMarketingConsentPreference = false;
      } catch (e) {}
    }
    if (state.variantPricing) this.variantPricing = { ...state.variantPricing };
    if (state.variantPricingHalf) this.variantPricingHalf = { ...state.variantPricingHalf };
    if (state.presentmentCurrencyCode) {
      this.presentmentCurrencyCode = String(state.presentmentCurrencyCode).trim().toUpperCase();
    }
    if (state.sellingPlanNames) this.sellingPlanNames = { ...state.sellingPlanNames };
    if (state.wizard2OfferBarPct != null && Number.isFinite(Number(state.wizard2OfferBarPct))) {
      this.wizard2OfferBarPct = Math.round(Number(state.wizard2OfferBarPct));
    } else {
      this.wizard2OfferBarPct = null;
    }
    if (typeof state.wizard2OfferBarFingerprint === 'string' && state.wizard2OfferBarFingerprint.length) {
      this.wizard2OfferBarFingerprint = state.wizard2OfferBarFingerprint;
    } else {
      this.wizard2OfferBarFingerprint = null;
    }
    // Migrate legacy selectedFlavors to selectedFlavorsTrial/Full
    if (state.selectedFlavors && state.selectedFlavors.length > 0 && this.selectedFlavorsTrial.length === 0) {
      this.selectedFlavorsTrial = [...state.selectedFlavors];
      this.selectedFlavorsFull = [...state.selectedFlavors];
    }
    // Legacy: half plan lived on selectedPlan — bowl is now planBowlValue
    if (this.selectedPlan === 'half') {
      if (!this.planBowlValue) this.planBowlValue = 'Half';
      this.selectedPlan = '1';
    }
    if (!['1', '2', '3'].includes(String(this.selectedPlan || ''))) {
      this.selectedPlan = '1';
    }
  }

  reset() {
    const preservedSessionCustomerId = this.sessionCustomerId;
    const preservedSessionOwnerName = this.sessionOwnerName;
    const preservedSessionOwnerEmail = this.sessionOwnerEmail;
    const preservedSessionEmailConsent = this.sessionEmailConsent;
    const preservedWizard2EmailMarketingConsentPreference = this.wizard2EmailMarketingConsentPreference;

    this.currentStep = 'name';
    this.petData = {
      name: '',
      gender: '',
      spayed: null,
      ageGroup: '',
      breed: '',
      breedUnknown: false,
      breedMixed: false,
      weight: null,
      composition: '',
      activity: '',
      isWorkingDog: false,
      allergies: [],
      snacks: '',
      ownerName: preservedSessionOwnerName || '',
      ownerEmail: preservedSessionOwnerEmail || '',
      emailConsent: preservedSessionEmailConsent !== undefined ? preservedSessionEmailConsent : true
    };
    this.calculations = {
      dailyKcal: null,
      dailyFoodG: null,
      daysPerBag: null,
      bagCount: null,
      matchDays: null,
      introBagCount: null,
      introMatchDays: null
    };
    this.selectedFlavorsTrial = [];
    this.selectedFlavorsFull = [];
    this.selectedPlan = null;
    this.planBowlValue = null;
    this.planBowlOptionValues = null;
    this.selectedPricePeriod = 'day';
    this.petMetaobjectId = null;
    this.customerId = null;
    this.variantPricing = {};
    this.variantPricingHalf = {};
    this.presentmentCurrencyCode = null;
    this.sessionCustomerId = preservedSessionCustomerId;
    this.sessionOwnerName = preservedSessionOwnerName;
    this.sessionOwnerEmail = preservedSessionOwnerEmail;
    this.sessionEmailConsent = preservedSessionEmailConsent;
    this.skippedEmailWithoutAddress = false;
    this.wizard2EmailMarketingConsentPreference = preservedWizard2EmailMarketingConsentPreference;
    this.sellingPlanNames = {};
  }

  clearSessionCustomerId() {
    this.sessionCustomerId = null;
    this.sessionOwnerName = null;
    this.sessionOwnerEmail = null;
    this.sessionEmailConsent = true;
  }

  /**
   * Possessive pronoun for copy like "Name zijn/haar plan" (NL) or "his/her plan" (EN).
   */
  getPronoun() {
    const locale = (typeof window !== 'undefined' && window.__wizard2CurrentLocale)
      ? String(window.__wizard2CurrentLocale).toLowerCase()
      : 'nl';
    const en = locale.startsWith('en');
    if (this.petData.gender === 'female') {
      return en ? 'her' : 'haar';
    }
    return en ? 'his' : 'zijn';
  }

  getPossessivePronoun() {
    return this.getPronoun();
  }

  getFurthestValidStep() {
    const stepOrder = [
      'name', 'spayed', 'age', 'breed', 'weight', 'composition',
      'activity', 'allergies', 'snacks', 'email', 'calculation',
      'plan_overview', 'plan_onetime', 'profile'
    ];
    const savedStep = this.currentStep || 'name';
    const savedStepIndex = stepOrder.indexOf(savedStep);
    if (savedStepIndex === -1) return 'name';

    let furthestValidStep = 'name';
    for (let i = 0; i <= savedStepIndex; i++) {
      const step = stepOrder[i];
      if (step === 'calculation') {
        if (this.validateStep('email')) furthestValidStep = step;
        else break;
      } else if (step === 'plan_overview' || step === 'plan_onetime') {
        const hasCalculations = this.calculations?.dailyFoodG != null;
        const hasTrialFlavors = this.selectedFlavorsTrial?.length > 0;
        const hasFullFlavors = this.selectedFlavorsFull?.length > 0;
        if (hasCalculations && (hasTrialFlavors || hasFullFlavors)) furthestValidStep = step;
        else break;
      } else if (step === 'profile') {
        furthestValidStep = step;
      } else {
        if (this.validateStep(step)) furthestValidStep = step;
        else break;
      }
    }
    return furthestValidStep;
  }
}

window.PetFlowState = PetFlowState;
